import { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  SearchX,
  FolderInput,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { PaperCard } from './PaperCard';
import { useFolders } from '@/features/sources/hooks/useFolders';
import type { SemanticScholarPaper, SearchResponse } from '@/features/search/hooks/useSearch';

interface SearchResultsProps {
  data: SearchResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  offset: number;
  limit: number;
  onPageChange: (newOffset: number) => void;
  onImport: (paper: SemanticScholarPaper, folderId?: string) => void;
  isImporting: boolean;
}

export function SearchResults({
  data,
  isLoading,
  isError,
  errorMessage,
  offset,
  limit,
  onPageChange,
  onImport,
  isImporting,
}: SearchResultsProps) {
  const [importTarget, setImportTarget] = useState<SemanticScholarPaper | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const { data: folders } = useFolders();

  const handleImportClick = (paper: SemanticScholarPaper) => {
    setImportTarget(paper);
    setSelectedFolderId('');
  };

  const handleConfirmImport = () => {
    if (!importTarget) return;
    onImport(importTarget, selectedFolderId || undefined);
    setImportTarget(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin mb-3" />
        <p className="text-sm">Searching papers...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <SearchX className="h-8 w-8 mb-3 text-rose-400" />
        <p className="text-sm font-medium text-slate-700">Search failed</p>
        <p className="text-xs mt-1">{errorMessage || 'Please try again or refine your query.'}</p>
      </div>
    );
  }

  if (!data || data.papers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <SearchX className="h-8 w-8 mb-3" />
        <p className="text-sm font-medium text-slate-700">No papers found</p>
        <p className="text-xs mt-1">Try different keywords or adjust your filters.</p>
      </div>
    );
  }

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(data.total / limit);
  const showingFrom = offset + 1;
  const showingTo = Math.min(offset + data.papers.length, data.total);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-slate-500">
        <p>
          Showing {showingFrom}-{showingTo} of {data.total.toLocaleString()} results
        </p>
        <p>
          Page {currentPage} of {totalPages}
        </p>
      </div>

      <div className="space-y-3">
        {data.papers.map((paper) => (
          <PaperCard
            key={paper.paperId}
            paper={paper}
            onImport={handleImportClick}
            isImporting={isImporting}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(0, offset - limit))}
            disabled={offset === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-slate-500 px-3">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(offset + limit)}
            disabled={offset + limit >= data.total}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      <Dialog open={!!importTarget} onOpenChange={(open) => !open && setImportTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderInput className="h-5 w-5" />
              Import paper
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 line-clamp-2">
              {importTarget?.title}
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Import to folder (optional)
              </label>
              <Select value={selectedFolderId} onValueChange={setSelectedFolderId}>
                <SelectTrigger>
                  <SelectValue placeholder="No folder (root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder (root)</SelectItem>
                  {folders?.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmImport} disabled={isImporting}>
              {isImporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
